import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "@tanstack/react-router";

interface PrivacyNoticeModalProps {
  onAccept: () => void;
  onDecline: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-[var(--stone-700)] mb-1">{title}</h3>
      {children}
    </div>
  );
}

export function PrivacyNoticeModal({ onAccept, onDecline }: PrivacyNoticeModalProps) {
  const { t } = useTranslation("portalAccess");
  const { t: tc } = useTranslation("common");
  const [accepted, setAccepted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const imprintLabel = tc("footer.imprint");
  const privacyPolicyLabel = tc("footer.privacyPolicy");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-[var(--stone-100)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--sage-100)] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[var(--sage-600)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--stone-800)]">
              {t("privacyModal.title")}
            </h2>
          </div>
          <p className="mt-3 text-sm text-[var(--stone-600)]">
            {t("privacyModal.intro")}
          </p>
          <p className="mt-3 text-sm text-[var(--stone-600)]">
            Vollständige Informationen:{" "}
            <Link to="/datenschutz" className="client-footer-link">
              {privacyPolicyLabel}
            </Link>{" "}
            <span aria-hidden="true">|</span>{" "}
            <Link to="/impressum" className="client-footer-link">
              {imprintLabel}
            </Link>
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <Section title={t("privacyModal.retention.title")}>
            <p className="text-sm text-[var(--stone-600)]">
              {t("privacyModal.retention.text")}
            </p>
          </Section>

          <Section title={t("privacyModal.rights.title")}>
            <p className="text-sm text-[var(--stone-600)]">
              {t("privacyModal.rights.text")}
            </p>
          </Section>

          {/* Expandable details */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm text-[var(--sage-600)] hover:text-[var(--sage-700)] font-medium"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {t("privacyModal.purposes.title")} & {t("privacyModal.data.title")}
          </button>

          {expanded && (
            <div className="space-y-4 pl-2 border-l-2 border-[var(--sage-200)]">
              <Section title={t("privacyModal.purposes.title")}>
                <ul className="text-sm text-[var(--stone-600)] list-disc pl-4 space-y-1">
                  {(t("privacyModal.purposes.items", { returnObjects: true }) as string[]).map(
                    (item) => <li key={item}>{item}</li>
                  )}
                </ul>
              </Section>
              <Section title={t("privacyModal.data.title")}>
                <ul className="text-sm text-[var(--stone-600)] list-disc pl-4 space-y-1">
                  {(t("privacyModal.data.items", { returnObjects: true }) as string[]).map(
                    (item) => <li key={item}>{item}</li>
                  )}
                </ul>
              </Section>
            </div>
          )}

          {/* Acceptance checkbox */}
          <label className="flex items-start gap-3 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-[var(--stone-300)] text-[var(--sage-600)] focus:ring-[var(--sage-500)]"
            />
            <span className="text-sm text-[var(--stone-700)] font-medium">
              {t("privacyModal.accept")}
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="p-6 pt-4 border-t border-[var(--stone-100)] flex gap-3">
          <button
            className="flex-1 h-12 px-6 rounded-xl font-medium text-sm border-2 border-[var(--stone-200)] text-[var(--stone-600)] hover:bg-[var(--stone-50)] transition-colors"
            onClick={onDecline}
          >
            {t("privacyModal.decline")}
          </button>
          <button
            className={`flex-1 h-12 px-6 rounded-xl font-medium text-sm transition-colors ${
              accepted
                ? "bg-[var(--sage-600)] text-white hover:bg-[var(--sage-700)]"
                : "bg-[var(--stone-200)] text-[var(--stone-400)] cursor-not-allowed"
            }`}
            disabled={!accepted}
            onClick={onAccept}
          >
            {t("privacyModal.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
